import { Button, Modal } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { ModalErrorBanner } from "app/pages/sales-intelligence/common-components/modals/ModalErrorBanner/ModalErrorBanner";

import type SignalFilters from "../../../models/core/SignalFilters";
import type UISignal from "../../../models/view/UISignal";
import {
    CUSTOM_STYLES,
    StyledConfirmationText,
    StyledErrorBannerWrapper,
    StyledFooter,
    StyledHeader,
    StyledMain,
    StyledModalContent,
    StyledTitle,
} from "./styles";

export type DeleteSignalModalProps = {
    errorMessage?: string;
    isDeleteInProgress: boolean;
    isOpen: boolean;
    signal: Readonly<UISignal<SignalFilters>> | null;
    onClose(): void;
    onSubmitClick(signal: Readonly<UISignal<SignalFilters>>): void;
};

const DeleteSignalModal = (props: DeleteSignalModalProps) => {
    const { errorMessage, isDeleteInProgress, isOpen, signal, onClose, onSubmitClick } = props;

    function handleSubmitClick() {
        if (signal !== null) {
            onSubmitClick(signal);
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={{
                overlay: { zIndex: 1120 },
                content: CUSTOM_STYLES.content,
            }}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={!isDeleteInProgress}
            data-automation="delete-signal-modal"
        >
            <StyledModalContent data-automation="delete-signal-modal-wrapper">
                <StyledHeader>
                    <I18n
                        data-automation="title-text"
                        element={StyledTitle}
                        translatable={{ key: "si.signals.delete-signal.title" }}
                    />
                </StyledHeader>
                <StyledMain>
                    {signal !== null ? (
                        <I18n
                            data-automation="confirmation-text"
                            element={StyledConfirmationText}
                            translatable={{
                                key: "si.common.delete-confirmation-with-name",
                                replacements: {
                                    name: signal.template.name,
                                },
                            }}
                        />
                    ) : null}
                    {errorMessage ? (
                        <StyledErrorBannerWrapper>
                            <ModalErrorBanner
                                message={errorMessage}
                                dataAutomation="delete-signal-modal-error"
                            />
                        </StyledErrorBannerWrapper>
                    ) : null}
                </StyledMain>
                <StyledFooter>
                    <Button
                        dataAutomation="cancel-button"
                        isDisabled={isDeleteInProgress}
                        type="flatDark"
                        onClick={onClose}
                    >
                        {translate("si.common.button.cancel")}
                    </Button>
                    <Button
                        dataAutomation="confirm-button"
                        isDisabled={isDeleteInProgress}
                        isLoading={isDeleteInProgress}
                        type="warning"
                        onClick={handleSubmitClick}
                    >
                        {translate("si.common.button.delete")}
                    </Button>
                </StyledFooter>
            </StyledModalContent>
        </Modal>
    );
};

export default DeleteSignalModal;
